import { FileWithId } from "@/app/(protected)/concepting/components/StepOne/types";
import { ENCODING_PREFIX_BASE64, FILE_TYPE } from "@/constants/constants";
import { getFileData, getOutputFileData } from "@/app/actions/actions";
import { ImageFormats } from "@/app/(protected)/generate-lifestyle/components/StepThree/components/TabsContent";
import { Images } from "@/app/(protected)/concepting/components/StepFour/types";
import { imageUrlEnum } from "@/app/(protected)/asset-generation/components/ReviewAssets/components/constants";
import { logger } from "@/lib/client-logger";

export const gcsFileURLPattern = new RegExp(String.raw`^gs://${process.env.GCP_STORAGE_BUCKET_NAME}/(?:.+/)*.+\.[a-zA-Z0-9]+$`);
export const gsGenericFilePattern = /^gs:\/\/([^/]+)\/(.+)$/; // pattern to match all types of gs links from other buckets

export const getGCSFileIdFromPath = (path: string): string => {
  const regex = new RegExp(`^gs://${process.env.GCP_STORAGE_BUCKET_NAME}/(.+)$`);
  const match = new RegExp(regex).exec(path);
  return match ? match[1] : "";
};

export const getFileNameFromPath = (path: string): string => {
  const match = new RegExp(/\/([^/]+)$/).exec(path);
  return match ? match[1] : "";
};

export const getFileExtensionFromPath = (path: string): string | null => {
  const fileName = getFileNameFromPath(path);
  const match = fileName ? new RegExp(/\.([a-zA-Z0-9]+)$/).exec(fileName) : null;
  return match ? match[1] : null;
};

// Utility function to get file extension
export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
};

/**
 * Helper to get the correct base64 property and input for getBase64FormatForImages.
 */
export const getBase64ByType = async (imageUrl: string, ext: string): Promise<string> => {
  const typeMap: Record<string, (url: string) => Promise<string>> = {
    png: async (url) => {
      const { pngBase64 } = await getBase64FormatForImages({ pngURL: url });
      if (!pngBase64) throw new Error("Failed to get PNG base64 data");
      return pngBase64;
    },
    jpg: async (url) => {
      const { jpegBase64 } = await getBase64FormatForImages({ jpegURL: url });
      if (!jpegBase64) throw new Error("Failed to get JPEG base64 data");
      return jpegBase64;
    },
    jpeg: async (url) => {
      const { jpegBase64 } = await getBase64FormatForImages({ jpegURL: url });
      if (!jpegBase64) throw new Error("Failed to get JPEG base64 data");
      return jpegBase64;
    },
    psd: async (url) => {
      const { psdBase64 } = await getBase64FormatForImages({ psdURL: url });
      if (!psdBase64) throw new Error("Failed to get PSD base64 data");
      return psdBase64;
    },
  };

  const handler = typeMap[ext];
  if (!handler) throw new Error(`Unsupported GCS image file type: ${ext}`);
  return handler(imageUrl);
};

/**
 * Validates a user-provided filename.
 *
 * This validation is intentionally limited to filename safety.
 * File extension, MIME type, magic-byte detection, and file-content
 * scanning should be handled separately.
 */

export function validateFilename(filename: string): boolean {
  const normalizedFilename = filename.normalize("NFC");

  const SAFE_FILENAME_PATTERN = /^[\p{L}\p{M}\p{N}][\p{L}\p{M}\p{N}._ ()-]*$/u;

  if (normalizedFilename.includes("/") || normalizedFilename.includes("\\") || normalizedFilename === "." || normalizedFilename === "..") {
    return false;
  }

  return SAFE_FILENAME_PATTERN.test(normalizedFilename);
}

// Function to validate file extension and type
export const validateFileExtensionAndType = (file: File, allowedTypes: string[]): boolean => {
  const extension = getFileExtension(file.name);
  const isValidType = allowedTypes.includes(file.type);
  const isValidExt = allowedTypes.includes(extension);
  return isValidType && isValidExt;
};

export const getInvalidFileTypeMessage = (allowedTypes: string[]): string => {
  const formatted = allowedTypes.length > 1 ? `${allowedTypes.slice(0, -1).join(", ")} or ${allowedTypes.at(-1)}` : allowedTypes[0];

  return `Please make sure your file is in image (${formatted}) format.`;
};

export const getMimeType = (fileName: string): string => {
  const extension = fileName?.split(".")?.pop()?.toLowerCase();
  if (extension === "png") {
    return "image/png";
  } else if (extension === "jpg" || extension === "jpeg") {
    return "image/jpeg";
  }
  return "application/octet-stream";
};

export const createFileWithId = (fileId: string, fileName: string, url: string, base64?: string): FileWithId => {
  const mimeType = getMimeType(fileName);

  let file: File;
  if (base64) {
    const byteString = atob(base64.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i); // NOSONAR
    }
    file = new File([ab], fileName, { type: mimeType });
  } else {
    file = new File([new ArrayBuffer(0)], fileName, { type: mimeType });
  }

  return {
    id: fileId ?? "",
    file,
    url: url ?? "",
    name: fileName ?? "",
    base64,
  };
};

export const cleanUploadTypes = (uploadType: string): string => {
  const cleanedUploadTypes = uploadType
    .replaceAll(".", "")
    .split(",")
    .map((type: string) => type.trim());
  return cleanedUploadTypes.join(" or ");
};

export const getBase64FormatForImages = async (images: { pngURL?: string; jpegURL?: string; psdURL?: string }) => {
  const { pngURL, jpegURL, psdURL } = images;

  const pngData = pngURL ? await getFileData(pngURL) : null;
  const jpegData = jpegURL ? await getFileData(jpegURL) : null;
  const psdData = psdURL ? await getFileData(psdURL) : null;

  if (pngURL && !pngData?.data) throw new Error("Failed to fetch PNG image data");
  if (jpegURL && !jpegData?.data) throw new Error("Failed to fetch JPEG image data");
  if (psdURL && !psdData?.data) throw new Error("Failed to fetch PSD image data");

  const toBase64 = (arrayBuffer: ArrayBuffer) => {
    const binary = new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCodePoint(byte), "");
    return btoa(binary);
  };

  const imageResponse = {
    ...(pngData?.data && { pngBase64: `${ENCODING_PREFIX_BASE64.PNG},${toBase64(pngData?.data)}` }),
    ...(jpegData?.data && { jpegBase64: `${ENCODING_PREFIX_BASE64.JPEG},${toBase64(jpegData?.data)}` }),
    ...(psdData?.data && { psdBase64: `${ENCODING_PREFIX_BASE64.PSD},${toBase64(psdData?.data)}` }),
  };

  return imageResponse;
};

const extractAssetIdFromUrl = (url: string): string | null => {
  const regex = /\/([^/]+\.(png|jpe?g|psd))$/i;
  const match = regex.exec(url);
  return match ? match[1] : null;
};

export const getBase64FormatForGeneratedImages = async (
  images: { pngURL?: string; jpegURL?: string; psdURL?: string },
  projectId: string,
  imageType: string,
) => {
  const { pngURL, jpegURL, psdURL } = images;

  const pngAssetId = pngURL ? extractAssetIdFromUrl(pngURL) : null;
  const jpegAssetId = jpegURL ? extractAssetIdFromUrl(jpegURL) : null;
  const psdAssetId = psdURL ? extractAssetIdFromUrl(psdURL) : null;

  const pngData = pngURL ? await getOutputFileData(projectId, pngAssetId, imageType) : null;
  const jpegData = jpegURL ? await getOutputFileData(projectId, jpegAssetId, imageType) : null;
  const psdData = psdURL ? await getOutputFileData(projectId, psdAssetId, imageType) : null;

  if (pngURL && !pngData?.data) throw new Error("Failed to fetch PNG image data");
  if (jpegURL && !jpegData?.data) throw new Error("Failed to fetch JPEG image data");
  if (psdURL && !psdData?.data) throw new Error("Failed to fetch PSD image data");

  const toBase64 = (arrayBuffer: ArrayBuffer) => {
    const binary = new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCodePoint(byte), "");
    return btoa(binary);
  };

  const imageResponse = {
    ...(pngData?.data && { pngBase64: `${ENCODING_PREFIX_BASE64.PNG},${toBase64(pngData?.data)}` }),
    ...(jpegData?.data && { jpegBase64: `${ENCODING_PREFIX_BASE64.JPEG},${toBase64(jpegData?.data)}` }),
    ...(psdData?.data && { psdBase64: `${ENCODING_PREFIX_BASE64.PSD},${toBase64(psdData?.data)}` }),
  };

  return imageResponse;
};

export const fetchBlobFromUrl = async (url: string): Promise<Blob | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file from URL: ${url}`);
    }
    return await response.blob();
  } catch (error) {
    logger.error("Error downloading file from URL:", error);
    return null;
  }
};

export const triggerDownload = (blob: Blob, filename: string): void => {
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(downloadUrl);
};

export const downloadFile = async (input: Blob | string, filename: string): Promise<void> => {
  let blob: Blob | null = null;

  if (input instanceof Blob) {
    blob = input;
  } else if (typeof input === "string") {
    blob = await fetchBlobFromUrl(input);
  } else {
    logger.error("Invalid input type, expected a Blob or a URL string");
    return;
  }

  if (blob) {
    triggerDownload(blob, filename);
  } else {
    logger.error("Failed to process the file for download.");
  }
};

export const downloadText = (fileName: string, text: string, fileType = FILE_TYPE.TXT) => {
  // We need to import CONTENT_TYPE or use strings here to avoid circular dependencies if any
  const element = document.createElement("a");
  const mimeType = fileType === FILE_TYPE.CSV ? "text/csv" : "text/plain";
  const extension = fileType === FILE_TYPE.CSV ? FILE_TYPE.CSV : FILE_TYPE.TXT;

  const file = new Blob([text], { type: mimeType });
  element.href = URL.createObjectURL(file);
  element.download = `${fileName}.${extension}`;
  document.body.appendChild(element); // Required for this to work in FireFox
  element.click();

  //Remove element from DOM
  element.remove();
};

export const downloadHTML = (fileName: string, text: string) => {
  const fullHtml = `<!DOCTYPE html>
      <html lang="en">
      <head><meta charset="UTF-8"><title>Blog Post</title>
      <style>body { font-family: sans-serif; }</style></head>
      <body>${text}</body>
      </html>`;

  const blob = new Blob([fullHtml], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result as string);

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };
  });
};

export const toJpeg = (fileName: string): string => {
  //regular expression to replace the file extension with .jpeg
  return fileName.replace(/\.[^/.]+$/, ".jpeg");
};

export const getFiles = (imageOutput: Images[] | Images, imageFormat: ImageFormats): string[] => {
  if (!imageOutput || (Array.isArray(imageOutput) && !imageOutput.length)) return [];

  const images = Array.isArray(imageOutput) ? imageOutput : [imageOutput];

  const formatToProperty: Record<string, keyof Images> = {
    [FILE_TYPE.PSD]: imageUrlEnum.PSD,
    [FILE_TYPE.JPG]: imageUrlEnum.JPEG,
    [FILE_TYPE.JPEG]: imageUrlEnum.JPEG,
  };

  const propertyName = formatToProperty[imageFormat.format ?? ""] ?? imageUrlEnum.PNG;

  const getURLsByFormat = (): string[] => {
    return images.map((file) => {
      // Check if selectedSizeOption exists and has corresponding size data
      if (file?.selectedSizeOption && file?.sizes?.[file.selectedSizeOption]) {
        // Access the specific size object and then the property
        const sizeData = file.sizes[file.selectedSizeOption];
        // Ensure sizeData is an object with the expected property
        if (sizeData && typeof sizeData === "object" && propertyName in sizeData) {
          return sizeData[propertyName] as string;
        }
      }
      return file?.[propertyName] as string;
    });
  };

  return getURLsByFormat().filter((url): url is string => !!url);
};

export const getUpscaledFilePrefix = (upscaledSize: string) => {
  const customSize = upscaledSize?.split("_");
  return (customSize && customSize.length > 1 ? `x${customSize[0]}` : upscaledSize) ?? "";
};

/**
 * Utility used to download images using base64.
 * Unused in the app.
 */
export const downloadImage = (fileName: string, imageData: string) => {
  const element = document.createElement("a");
  element.href = imageData;
  element.download = `${fileName}.${String(imageData)?.split("data:image/")[1]?.split(";")[0]}`;
  document.body.appendChild(element); // Required for this to work in Firefox
  element.click();
  element.remove(); // Clean up
};
